'use client';

import { useRouter, useSearchParams } from 'next/navigation';
import React, { Suspense, useState } from 'react';
import { login } from '@/lib/auth/client';

function LoginForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const next = searchParams?.get('next') || '/admin';

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username.trim() || !password) return;

    setError(null);
    setLoading(true);

    try {
      await login(username.trim(), password);
      // Only allow same-origin relative paths, so ?next= cannot be used to
      // bounce a freshly-authenticated admin to an attacker's site.
      const target = next.startsWith('/') && !next.startsWith('//') ? next : '/admin';
      router.replace(target);
    } catch (err: any) {
      setError(err.message || 'Login failed');
      setLoading(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '12px 14px',
    borderRadius: '8px',
    border: '1px solid rgba(255, 255, 255, 0.14)',
    backgroundColor: '#000000',
    color: '#fff',
    fontSize: '0.95rem',
    outline: 'none',
  };

  return (
    <div
      style={{
        backgroundColor: '#0A0F1A',
        border: '1px solid rgba(255, 255, 255, 0.12)',
        borderRadius: '16px',
        padding: '2.25rem',
        maxWidth: '420px',
        width: '100%',
        boxShadow: '0 20px 50px rgba(0,0,0,0.7)',
      }}
    >
      <div style={{ textAlign: 'center', marginBottom: '1.75rem' }}>
        <img
          src="/images/esabha-logo.png"
          alt="E-Sabha Logo"
          style={{ height: '54px', width: 'auto', objectFit: 'contain', marginBottom: '10px' }}
        />
        <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color: '#FFFFFF' }}>
          Host Sign In
        </h1>
        <p style={{ margin: '6px 0 0 0', fontSize: '0.875rem', color: '#94A3B8' }}>
          Administrator access to scheduling and host controls
        </p>
      </div>

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <div>
          <label
            htmlFor="username"
            style={{
              display: 'block',
              fontSize: '0.85rem',
              fontWeight: 500,
              color: '#FFFFFF',
              marginBottom: '6px',
            }}
          >
            Username
          </label>
          <input
            id="username"
            type="text"
            autoComplete="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
            disabled={loading}
            style={inputStyle}
          />
        </div>

        <div>
          <label
            htmlFor="password"
            style={{
              display: 'block',
              fontSize: '0.85rem',
              fontWeight: 500,
              color: '#FFFFFF',
              marginBottom: '6px',
            }}
          >
            Password
          </label>
          <input
            id="password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            disabled={loading}
            style={inputStyle}
          />
        </div>

        {error && (
          <div
            style={{
              color: '#EF4444',
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              padding: '10px 14px',
              borderRadius: '8px',
              fontSize: '0.85rem',
              border: '1px solid rgba(239, 68, 68, 0.2)',
            }}
          >
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          style={{
            marginTop: '0.5rem',
            width: '100%',
            padding: '14px',
            borderRadius: '10px',
            backgroundColor: '#F59E0B',
            color: '#000',
            fontWeight: 700,
            fontSize: '1rem',
            border: 'none',
            cursor: loading ? 'default' : 'pointer',
            opacity: loading ? 0.7 : 1,
            boxShadow: '0 2px 10px rgba(245, 158, 11, 0.3)',
          }}
        >
          {loading ? 'Signing in...' : 'Sign In'}
        </button>
      </form>
    </div>
  );
}

export default function LoginPage() {
  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: '#000000',
        color: '#FFFFFF',
        display: 'grid',
        placeItems: 'center',
        padding: '2rem 1rem',
        fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
        backgroundImage:
          'radial-gradient(ellipse at 50% 20%, rgba(0, 63, 178, 0.25) 0%, rgba(0, 199, 156, 0.08) 35%, transparent 70%)',
      }}
    >
      <Suspense fallback={<div style={{ color: '#94A3B8' }}>Loading...</div>}>
        <LoginForm />
      </Suspense>
    </div>
  );
}
